import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { ChevronDown, Funnel, Loader2 } from "lucide-react";
import Modal from "@/components/Modal";
import AdminEmptyState from "@/components/AdminEmptyState";
import StudentRedeemedCouponCard from "@/components/student/StudentRedeemedCouponCard";
import StudentRedeemedSponsorCard from "@/components/student/StudentRedeemedSponsorCard";
import { GetRedeemedCouponsAPI } from "@/services/api/coupon/getRedeemedCoupons";
import {
  GetRedeemedSponsorsAPI,
  type RedeemedSponsor,
} from "@/services/api/sponsor/getRedeemedSponsors";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import {
  buildRedeemedCouponQrValue,
  isRedeemedCouponExpired,
  isRedeemedCouponUsed,
  type RedeemedCouponRecord,
} from "@/utils/couponFormat";
import { getActivityStatus } from "@/utils/sponsorStatus";

const COUPON_PAGE_SIZE = 10;
const SPONSOR_PAGE_SIZE = 10;
/** `getRedeemedSponsors` answers with this status when nothing was redeemed. */
const NO_REDEEMED_SPONSORS_STATUS = -40004;

/** Still usable: not consumed by a shop scan and not past its expiry. */
type CouponStatus = "active" | "used" | "expired";
/** `getActivityStatus` reports a redeemed sponsor as still running while pending. */
type SponsorStatus = "in-progress" | "completed" | "failed";

const COUPON_FILTERS: { key: "all" | CouponStatus; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "used", label: "Used" },
  { key: "expired", label: "EXPIRED" },
];

const SPONSOR_FILTERS: { key: "all" | SponsorStatus; label: string }[] = [
  { key: "all", label: "All" },
  { key: "in-progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
];

/**
 * The one filter behind the page: which redeemed type to show and, inside it,
 * which status. A single dropdown carries every type/status pair, so exactly
 * one of the two sections is on screen.
 */
type Filter =
  | { type: "coupon"; status: "all" | CouponStatus }
  | { type: "sponsor"; status: "all" | SponsorStatus };

/** Selectable dropdown keys are compound: `coupon:all`, `sponsor:in-progress`, … */
const filterKey = (type: Filter["type"], status: string) => `${type}:${status}`;

const parseFilterKey = (key: string): Filter => {
  const [type, status] = key.split(":");
  return type === "sponsor"
    ? { type: "sponsor", status: status as "all" | SponsorStatus }
    : { type: "coupon", status: status as "all" | CouponStatus };
};

const COUPON_BADGE: Record<CouponStatus, string> = {
  active: "bg-[#4CAF50] text-white",
  used: "bg-gray-500 text-white",
  expired: "bg-gray-400 text-white",
};

const SPONSOR_BADGE: Record<SponsorStatus, string> = {
  "in-progress": "bg-[#ffa217] text-white",
  completed: "bg-[#4CAF50] text-white",
  failed: "bg-[#E53935] text-white",
};

/** Filter dropdown dots reuse the badge colours above. */
const COUPON_DOT: Record<CouponStatus, string> = {
  active: "bg-[#4CAF50]",
  used: "bg-gray-500",
  expired: "bg-gray-400",
};

const SPONSOR_DOT: Record<SponsorStatus, string> = {
  "in-progress": "bg-[#ffa217]",
  completed: "bg-[#4CAF50]",
  failed: "bg-[#E53935]",
};

const ALL_DOT = "bg-gray-300";

/**
 * `redeem_records.used` is 0/false once a shop has scanned the coupon, which
 * wins over the expiry date: a consumed coupon reads as used, not expired.
 */
const getCouponStatus = (record: RedeemedCouponRecord): CouponStatus => {
  if (isRedeemedCouponUsed(record)) return "used";
  if (isRedeemedCouponExpired(record)) return "expired";
  return "active";
};

/**
 * Mirrors `StudentRedeemedSponsorCard`: an approved compensation or a fully
 * completed task set is completed, an entirely failed task set is failed, and
 * anything still unresolved is in progress.
 */
const getSponsorStatus = (sponsor: RedeemedSponsor): SponsorStatus => {
  const activity = sponsor.activity;
  if (activity?.compensation_approval_status === "approved") return "completed";
  const status = getActivityStatus(
    Array.isArray(activity?.tasks) ? activity.tasks : [],
    activity?.activity_date ?? null,
  ).status;
  if (status === "completed") return "completed";
  if (status === "failed") return "failed";
  return "in-progress";
};

/** Card-shaped pulse placeholder for the coupon / sponsor card grids. */
const CardGridSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
      >
        <div className="flex items-center gap-3">
          <div className="h-16 w-16 shrink-0 rounded-xl bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-3/5 rounded-lg bg-gray-100" />
            <div className="h-3 w-2/5 rounded-md bg-gray-100" />
            <div className="h-3 w-4/5 rounded-md bg-gray-100" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="h-3 w-1/3 rounded-md bg-gray-100" />
          <div className="h-8 w-20 rounded-xl bg-gray-100" />
        </div>
      </div>
    ))}
  </>
);

type StatusFilterOption = {
  key: string;
  label: string;
  /** Type prefix in front of the status, e.g. "Coupon" + "Active". */
  typeLabel?: string;
  dot?: string;
  count?: number;
  /** Group separator: a muted label row, not a selectable option. */
  header?: boolean;
};

/**
 * Status filter: a persistent filter icon button that shows the selected type
 * and status and opens a dropdown of every type/status pair with its count.
 * Group headers split the coupon rows from the sponsor rows. A transparent
 * backdrop and `Escape` close the panel.
 */
const StatusFilter: React.FC<{
  options: StatusFilterOption[];
  active: string;
  onChange: (key: string) => void;
}> = ({ options, active, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected =
    options.find((option) => !option.header && option.key === active) ??
    options.find((option) => !option.header);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={t("Filter")}
          title={t("Filter")}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-[#ffa217] hover:text-[#ffa217]"
        >
          <Funnel size={14} className="shrink-0 text-[#ffa217]" />
          {selected && (
            <>
              {t(selected.typeLabel ?? "")}
              <span className="text-gray-300">•</span>
              {t(selected.label)}
            </>
          )}
          <ChevronDown
            size={13}
            className={`shrink-0 transition ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div
            role="listbox"
            aria-label={t("Filter")}
            className="absolute left-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-gray-100 bg-white py-1 shadow-lg"
          >
            {options.map((option, index) =>
              option.header ? (
                <div
                  key={option.key}
                  className={`bg-gray-50 px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 ${
                    index > 0 ? "border-t border-gray-100" : ""
                  }`}
                >
                  {t(option.label)}
                </div>
              ) : (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={option.key === active}
                  onClick={() => {
                    onChange(option.key);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold transition hover:bg-gray-50 ${
                    option.key === active ? "text-[#ffa217]" : "text-gray-700"
                  }`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${option.dot ?? ""}`}
                  />
                  <span className="text-gray-400">{option.typeLabel}</span>
                  <span className="flex-1">{t(option.label)}</span>
                  <span className="text-gray-400">{option.count}</span>
                </button>
              ),
            )}
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-40"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

const SectionEmptyState: React.FC<{
  iconName: string;
  title: string;
  message: string;
  onRefresh: () => void;
}> = ({ iconName, title, message, onRefresh }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-gray-100 bg-white">
      <AdminEmptyState
        iconName={iconName}
        title={title}
        message={message}
        actionLabel={t("Refresh")}
        onAction={onRefresh}
      />
    </div>
  );
};

/**
 * My Redemptions — everything the student has redeemed: the redeemed coupons
 * (still usable, used, or expired) and the redeemed sponsors (in progress,
 * completed or failed), each card carrying a status badge. One dropdown picks
 * the type and the status at once, so a single section is shown.
 */
const StudentRedemptionsPage: React.FC = () => {
  const { t } = useTranslation();

  const [coupons, setCoupons] = useState<RedeemedCouponRecord[]>([]);
  const [couponPage, setCouponPage] = useState(1);
  const [couponHasMore, setCouponHasMore] = useState(false);
  const [couponLoading, setCouponLoading] = useState(true);

  const [sponsors, setSponsors] = useState<RedeemedSponsor[]>([]);
  const [sponsorPage, setSponsorPage] = useState(1);
  const [sponsorHasMore, setSponsorHasMore] = useState(false);
  const [sponsorLoading, setSponsorLoading] = useState(true);

  const [filter, setFilter] = useState<Filter>({ type: "sponsor", status: "all" });

  const [qrRecord, setQrRecord] = useState<RedeemedCouponRecord | null>(null);

  const couponSentinelRef = useRef<HTMLDivElement>(null);
  const sponsorSentinelRef = useRef<HTMLDivElement>(null);

  const fetchCoupons = useCallback(
    async (targetPage: number, mode: "initial" | "more") => {
      setCouponLoading(true);
      const resp = await GetRedeemedCouponsAPI(targetPage);
      setCouponLoading(false);
      const result = resp?.result;
      if (resp?.status !== 0 || !Array.isArray(result)) {
        setCouponHasMore(false);
        return;
      }
      setCouponPage(targetPage);
      setCouponHasMore(result.length >= COUPON_PAGE_SIZE);
      setCoupons((prev) => (mode === "more" ? [...prev, ...result] : result));
    },
    [],
  );

  const fetchSponsors = useCallback(
    async (targetPage: number, mode: "initial" | "more") => {
      setSponsorLoading(true);
      const resp = await GetRedeemedSponsorsAPI(targetPage, SPONSOR_PAGE_SIZE);
      setSponsorLoading(false);
      if (resp?.status === NO_REDEEMED_SPONSORS_STATUS) {
        setSponsorHasMore(false);
        return;
      }
      const result = resp?.result?.sponsors;
      if (resp?.status !== 0 || !Array.isArray(result)) {
        setSponsorHasMore(false);
        return;
      }
      setSponsorPage(targetPage);
      setSponsorHasMore(result.length >= SPONSOR_PAGE_SIZE);
      setSponsors((prev) => (mode === "more" ? [...prev, ...result] : result));
    },
    [],
  );

  useEffect(() => {
    fetchCoupons(1, "initial");
    fetchSponsors(1, "initial");
  }, [fetchCoupons, fetchSponsors]);

  useEffect(() => {
    const sentinel = couponSentinelRef.current;
    if (!sentinel) return;

    const maybeLoadMore = () => {
      if (couponHasMore && !couponLoading) {
        fetchCoupons(couponPage + 1, "more");
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      const onScroll = () => {
        const reachedBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 400;
        if (reachedBottom) maybeLoadMore();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchCoupons, couponHasMore, couponLoading, couponPage]);

  useEffect(() => {
    const sentinel = sponsorSentinelRef.current;
    if (!sentinel) return;

    const maybeLoadMore = () => {
      if (sponsorHasMore && !sponsorLoading) {
        fetchSponsors(sponsorPage + 1, "more");
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      const onScroll = () => {
        const reachedBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 400;
        if (reachedBottom) maybeLoadMore();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchSponsors, sponsorHasMore, sponsorLoading, sponsorPage]);

  const refreshAll = async () => {
    await fetchCoupons(1, "initial");
    await fetchSponsors(1, "initial");
  };

  const { refreshing, touchProps } = usePullToRefresh(refreshAll);

  const couponEntries = useMemo(
    () => coupons.map((record) => ({ record, status: getCouponStatus(record) })),
    [coupons],
  );
  const sponsorEntries = useMemo(
    () => sponsors.map((sponsor) => ({ sponsor, status: getSponsorStatus(sponsor) })),
    [sponsors],
  );

  const countCoupon = useCallback(
    (status: CouponStatus) =>
      couponEntries.filter((item) => item.status === status).length,
    [couponEntries],
  );
  const countSponsor = useCallback(
    (status: SponsorStatus) =>
      sponsorEntries.filter((item) => item.status === status).length,
    [sponsorEntries],
  );

  const filterOptions = useMemo<StatusFilterOption[]>(
    () => [
      { key: "__coupon_group", label: "Coupons", header: true },
      ...COUPON_FILTERS.map((option) => ({
        key: filterKey("coupon", option.key),
        typeLabel: "Coupon",
        label: option.label,
        dot: option.key === "all" ? ALL_DOT : COUPON_DOT[option.key],
        count: option.key === "all" ? coupons.length : countCoupon(option.key),
      })),
      { key: "__sponsor_group", label: "Sponsors", header: true },
      ...SPONSOR_FILTERS.map((option) => ({
        key: filterKey("sponsor", option.key),
        typeLabel: "Sponsor",
        label: option.label,
        dot: option.key === "all" ? ALL_DOT : SPONSOR_DOT[option.key],
        count: option.key === "all" ? sponsors.length : countSponsor(option.key),
      })),
    ],
    [coupons.length, sponsors.length, countCoupon, countSponsor],
  );

  const activeKey = filterKey(filter.type, filter.status);

  const visibleCoupons = useMemo(
    () =>
      filter.type === "coupon"
        ? couponEntries.filter(
            (item) => filter.status === "all" || item.status === filter.status,
          )
        : [],
    [couponEntries, filter],
  );

  const visibleSponsors = useMemo(
    () =>
      filter.type === "sponsor"
        ? sponsorEntries.filter(
            (item) => filter.status === "all" || item.status === filter.status,
          )
        : [],
    [sponsorEntries, filter],
  );

  return (
    <div className="mx-auto max-w-4xl px-4 py-6" {...touchProps}>
      <h1 className="text-2xl font-bold text-gray-900">{t("My Redemptions")}</h1>

      {refreshing && (
        <div className="mt-3 flex justify-center">
          <Loader2 size={18} className="animate-spin text-[#ffa217]" />
        </div>
      )}

      <div className="mt-6">
        <StatusFilter
          options={filterOptions}
          active={activeKey}
          onChange={(key) => setFilter(parseFilterKey(key))}
        />
      </div>

      {filter.type === "coupon" ? (
        <section className="mt-6">
          <h2 className="text-base font-bold text-gray-900">
            {t("My redeemed coupons")}
          </h2>

          {couponLoading && coupons.length === 0 ? (
            <CardGridSkeleton count={2} />
          ) : coupons.length === 0 ? (
            <SectionEmptyState
              iconName="ticket-outline"
              title={t("No redeemed coupons")}
              message={t("Redeem a coupon to see it here")}
              onRefresh={() => fetchCoupons(1, "initial")}
            />
          ) : (
            <>
              {visibleCoupons.length === 0 ? (
                <div className="mt-3">
                  <SectionEmptyState
                    iconName="ticket-outline"
                    title={t("No redeemed coupons")}
                    message={t("Redeem a coupon to see it here")}
                    onRefresh={() => fetchCoupons(1, "initial")}
                  />
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleCoupons.map(({ record, status }) => (
                    <div key={record.id} className="relative">
                      <StudentRedeemedCouponCard
                        record={record}
                        onShowQr={setQrRecord}
                      />
                      <span
                        className={`absolute left-2.5 top-2.5 rounded-full px-2.5 py-1 text-[11px] font-bold shadow-sm ${COUPON_BADGE[status]}`}
                      >
                        {t(
                          status === "active"
                            ? "Active"
                            : status === "used"
                              ? "Used"
                              : "EXPIRED",
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div ref={couponSentinelRef} />
            </>
          )}
        </section>
      ) : (
        <section className="mt-6">
          <h2 className="text-base font-bold text-gray-900">
            {t("My redeemed sponsors")}
          </h2>

          {sponsorLoading && sponsors.length === 0 ? (
            <CardGridSkeleton count={2} />
          ) : sponsors.length === 0 ? (
            <SectionEmptyState
              iconName="inbox-outline"
              title={t("No redeemed sponsors")}
              message={t("Redeem a sponsor to see it here")}
              onRefresh={() => fetchSponsors(1, "initial")}
            />
          ) : (
            <>
              {visibleSponsors.length === 0 ? (
                <div className="mt-3">
                  <SectionEmptyState
                    iconName="inbox-outline"
                    title={t("No redeemed sponsors")}
                    message={t("Redeem a sponsor to see it here")}
                    onRefresh={() => fetchSponsors(1, "initial")}
                  />
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleSponsors.map(({ sponsor, status }) => (
                    <div key={sponsor.id} className="relative">
                      <StudentRedeemedSponsorCard sponsor={sponsor} />
                      <span
                        className={`absolute left-2.5 top-2.5 rounded-full px-2.5 py-1 text-[11px] font-bold shadow-sm ${SPONSOR_BADGE[status]}`}
                      >
                        {t(
                          status === "completed"
                            ? "Completed"
                            : status === "failed"
                              ? "Failed"
                              : "In Progress",
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div ref={sponsorSentinelRef} />
            </>
          )}
        </section>
      )}

      <Modal visible={Boolean(qrRecord)} onClose={() => setQrRecord(null)}>
        {qrRecord && (
          <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
            <div className="mb-3 text-sm font-semibold text-gray-900">
              {String(qrRecord.coupon_info?.coupon_name ?? t("Coupon"))}
            </div>
            <div className="inline-block rounded-xl border border-gray-100 bg-white p-3">
              <QRCode value={buildRedeemedCouponQrValue(qrRecord)} size={200} />
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {t("Show this QR code to the shop staff")}
            </p>
            <button
              type="button"
              onClick={() => setQrRecord(null)}
              className="mt-4 w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
            >
              {t("Close")}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StudentRedemptionsPage;
